import React, { useEffect, useState } from "react";
import { popsApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sparkles, Play, Save, Trash2, Table2 } from "lucide-react";
import { toast } from "sonner";

const DIMS = ["customer", "provider", "service", "service_category", "project", "region", "month", "env"];
const MEASURES = ["usage_qty", "list_cost", "msp_cost", "customer_price", "margin_usd", "margin_pct"];

const fmtCell = (m, v) => {
  if (v == null) return "—";
  if (m === "margin_pct") return `${v}%`;
  if (m === "usage_qty") return Number(v).toLocaleString();
  return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export default function ReportBuilderTab() {
  const [def, setDef] = useState({ group_by: ["customer", "service_category"], measures: ["customer_price", "msp_cost", "margin_usd", "margin_pct"], filters: {}, last_n_months: 3 });
  const [nl, setNl] = useState("");
  const [nlSource, setNlSource] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [views, setViews] = useState([]);
  const [viewName, setViewName] = useState("");
  const [cadence, setCadence] = useState("none");

  const loadViews = () => popsApi.views().then(setViews).catch(console.error);
  useEffect(() => { loadViews(); }, []);

  const compile = async () => {
    if (!nl.trim()) return;
    setBusy(true);
    try {
      const out = await popsApi.nlReport(nl);
      setDef(out.definition);
      setNlSource(out.source);
      toast.success(`Definition compiled (${out.source})`, { description: "Inspect & edit below, then run — no black-box results." });
    } catch (e) {
      toast.error("Compile failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      setResult(await popsApi.runReport(def));
    } catch (e) {
      toast.error("Run failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const saveView = async () => {
    if (!viewName.trim()) {
      toast.error("Name the view first");
      return;
    }
    try {
      await popsApi.saveView(viewName, def, cadence === "none" ? null : { cadence, hour: 8, recipients: "billing@msp.example" });
      toast.success("View saved" + (cadence !== "none" ? ` — scheduled ${cadence} delivery` : ""));
      setViewName("");
      loadViews();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const toggleDim = (d) => {
    const g = def.group_by.includes(d) ? def.group_by.filter((x) => x !== d) : [...def.group_by, d].slice(0, 5);
    setDef({ ...def, group_by: g.length ? g : ["customer"] });
  };
  const toggleMeasure = (m) => {
    const ms = def.measures.includes(m) ? def.measures.filter((x) => x !== m) : [...def.measures, m];
    setDef({ ...def, measures: ms.length ? ms : ["customer_price"] });
  };

  const exportCsv = () => {
    if (!result) return;
    const cols = [...result.definition.group_by, ...result.definition.measures];
    const rows = [cols.join(","), ...result.rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "cloudscore_partner_report.csv";
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="pops-reports-tab">
      {/* NL builder */}
      <div className="cs-card p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Natural-language report builder</h3>
          {nlSource && <span className="cs-mono text-[10px] uppercase text-muted-foreground ml-auto">compiled by: {nlSource}</span>}
        </div>
        <div className="flex gap-2">
          <Input value={nl} onChange={(e) => setNl(e.target.value)}
            placeholder='e.g., "margin by service for Acme, last 6 months, monthly"'
            data-testid="nl-prompt-input" className="rounded-sm text-sm" />
          <Button onClick={compile} disabled={busy} data-testid="nl-compile-button"
            className="rounded-full bg-[#00579e] hover:bg-[#004580] text-white text-xs px-5 shrink-0">
            Compile
          </Button>
        </div>
      </div>

      {/* Definition editor */}
      <div className="cs-card p-5" data-testid="report-definition-editor">
        <h3 className="cs-heading text-sm font-semibold mb-3">Definition <span className="text-[11px] text-muted-foreground font-normal">— group by up to 5 levels (Advanced Grouping parity)</span></h3>
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">Group by ({def.group_by.length}/5)</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {DIMS.map((d) => (
            <button key={d} onClick={() => toggleDim(d)} data-testid={`dim-${d}`}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors duration-200 ${def.group_by.includes(d) ? "bg-[#00579e] text-white border-[#00579e]" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {d}{def.group_by.includes(d) ? ` · ${def.group_by.indexOf(d) + 1}` : ""}
            </button>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">Measures</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {MEASURES.map((m) => (
            <button key={m} onClick={() => toggleMeasure(m)} data-testid={`measure-${m}`}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors duration-200 ${def.measures.includes(m) ? "bg-[#0f766e] text-white border-[#0f766e]" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {m}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Window</span>
            <Select value={String(def.last_n_months)} onValueChange={(v) => setDef({ ...def, last_n_months: parseInt(v) })}>
              <SelectTrigger className="rounded-sm h-8 text-xs w-[130px]" data-testid="window-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 3, 6, 12].map((n) => <SelectItem key={n} value={String(n)}>last {n} month{n > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {def.filters.customer && (
            <span className="cs-pill bg-[#e0f2fe] text-[#0369a1] text-[10px]">
              customer = {def.filters.customer}
              <button className="ml-1" onClick={() => setDef({ ...def, filters: {} })}>×</button>
            </span>
          )}
          <Button onClick={run} disabled={busy} data-testid="run-report-button"
            className="rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-5 ml-auto">
            <Play className="w-3.5 h-3.5 mr-1" /> {busy ? "Running…" : "Run report"}
          </Button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="cs-card overflow-hidden" data-testid="report-results">
          <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
            <Table2 className="w-4 h-4 text-[#00579e]" />
            <span className="text-sm font-semibold">{result.row_count} rows · {result.periods[0]} → {result.periods[result.periods.length - 1]}</span>
            <button onClick={exportCsv} data-testid="export-report-csv" className="ml-auto text-xs text-[#00579e] hover:underline">Export CSV</button>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="Save as view…"
                data-testid="view-name-input" className="rounded-sm h-8 text-xs w-[180px]" />
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger className="rounded-sm h-8 text-xs w-[140px]" data-testid="cadence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">no schedule</SelectItem>
                  <SelectItem value="daily">daily 08:00</SelectItem>
                  <SelectItem value="weekly">weekly 08:00</SelectItem>
                  <SelectItem value="monthly">monthly 08:00</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={saveView} data-testid="save-view-button" className="rounded-full h-8 text-xs">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border sticky top-0">
                <tr className="text-left">
                  {result.definition.group_by.map((g) => <th key={g} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{g}</th>)}
                  {result.definition.measures.map((m) => <th key={m} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground text-right">{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    {result.definition.group_by.map((g) => <td key={g} className="px-3 py-1.5 font-medium">{r[g]}</td>)}
                    {result.definition.measures.map((m) => (
                      <td key={m} className={`px-3 py-1.5 text-right cs-mono ${m.startsWith("margin") && r[m] < 0 ? "text-[#b91c1c]" : ""}`}>{fmtCell(m, r[m])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td colSpan={result.definition.group_by.length} className="px-3 py-2 text-right">TOTAL</td>
                  {result.definition.measures.map((m) => (
                    <td key={m} className="px-3 py-2 text-right cs-mono">{fmtCell(m, result.totals[m])}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Saved views */}
      <div className="cs-card p-5" data-testid="saved-views-panel">
        <h3 className="cs-heading text-sm font-semibold mb-3">Saved views (pre-materialized · scheduled delivery)</h3>
        {views.length === 0 && <div className="text-xs text-muted-foreground" data-testid="no-views">No saved views yet.</div>}
        <div className="space-y-2">
          {views.map((v) => (
            <div key={v.id} className="flex flex-wrap items-center gap-2 border border-border rounded-sm px-3 py-2" data-testid={`view-row-${v.id}`}>
              <span className="text-xs font-semibold">{v.name}</span>
              <span className="cs-mono text-[10px] text-muted-foreground">
                {v.definition.group_by.join(" → ")} · {v.definition.last_n_months}mo
              </span>
              {v.schedule && <span className="cs-pill bg-[#ede9fe] text-[#6d28d9] text-[9px]">{v.schedule.cadence} @ {v.schedule.hour}:00</span>}
              {v.last_delivery && <span className="text-[10px] text-muted-foreground">last: {v.last_delivery.rows} rows via {v.last_delivery.via.split(" ")[0]}</span>}
              <div className="ml-auto flex gap-2">
                <button data-testid={`run-view-${v.id}`} className="text-[11px] text-[#00579e] hover:underline"
                  onClick={async () => {
                    const out = await popsApi.runView(v.id);
                    setResult(out.result);
                    setDef(out.view.definition);
                    toast.success(`View "${v.name}" delivered (${out.result.row_count} rows)`);
                    loadViews();
                  }}>run now</button>
                <button data-testid={`delete-view-${v.id}`} className="text-muted-foreground hover:text-[#b91c1c]"
                  onClick={async () => { await popsApi.deleteView(v.id); loadViews(); }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
